import { Capacitor } from '@capacitor/core';

// Static iOS import - mirror revenueCatService: avoid dynamic-import races on
// the iOS WKWebView. The plugin also registers a web stub, so a static import
// is safe in the web bundle; we simply never call into it off native iOS.
import {
  AdMob,
  AdmobConsentStatus,
  RewardAdPluginEvents,
} from '@capacitor-community/admob';

import {
  trackRewardedAdWatched,
  trackRewardedRewardEarned,
} from './analytics';

// iOS rewarded-ad shim.
//
// The free tier is monetised on iOS by an opt-in rewarded ad-gate (see
// useRewardedGate): the 2nd+ Smart Schedule run per month and the 2nd+
// concurrent upcoming round prompt a rewarded ad ("watch to continue, or go
// Pro"). There are NO inline / banner / interstitial ads anywhere.
//
// Everything here is a no-op off native iOS. Web ads use AdSense (AdSlot.jsx).
//
// Ad unit IDs come from Vite env in production; in dev/staging we fall back to
// Google's official test IDs and force test mode so we never serve (or click)
// a live ad while developing.

const IS_NATIVE_IOS =
  Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';

// Google's official iOS test rewarded unit - safe to show/click in dev.
const TEST_REWARDED_IOS = 'ca-app-pub-3940256099942544/1712485313';

const REAL_REWARDED_IOS = import.meta.env.VITE_ADMOB_REWARDED_IOS || '';
const REWARDED_AD_ID = REAL_REWARDED_IOS || TEST_REWARDED_IOS;

// Test mode unless a real prod unit is configured AND we're not in a dev build.
const USE_TEST_ADS = !REAL_REWARDED_IOS || import.meta.env.DEV;

let initPromise = null;
let rewardedReady = false;

// True only where rewarded ads can run (native iOS). Callers use this to
// decide whether a gate even applies.
export const isAdsAvailable = () => IS_NATIVE_IOS;

/**
 * Initialise the Google Mobile Ads SDK, then request UMP (GDPR) consent and
 * the iOS App Tracking Transparency prompt, in that order. Safe to call
 * repeatedly - the work runs once. No-op off native iOS. Consent/ATT failures
 * are non-fatal (ads still serve non-personalised).
 */
export const initAds = async () => {
  if (!IS_NATIVE_IOS) return false;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      await AdMob.initialize({ initializeForTesting: USE_TEST_ADS });

      // UMP consent (EU/GDPR). Show the form only when a choice is required.
      try {
        const info = await AdMob.requestConsentInfo();
        if (info?.isConsentFormAvailable && info?.status === AdmobConsentStatus.REQUIRED) {
          await AdMob.showConsentForm();
        }
      } catch (err) {
        console.warn('[Ads] UMP consent flow failed (continuing):', err?.message || err);
      }

      // iOS App Tracking Transparency. Must follow the consent step.
      try {
        await AdMob.requestTrackingAuthorization();
      } catch (err) {
        console.warn('[Ads] ATT request failed (continuing):', err?.message || err);
      }

      return true;
    } catch (err) {
      console.error('[Ads] initialise failed:', err?.message || err);
      initPromise = null;
      return false;
    }
  })();

  return initPromise;
};

/**
 * Preload a rewarded ad so a gate can show it instantly. No-op off native iOS.
 * Best-effort: failures leave `rewardedReady` false and the gate fails open.
 */
export const preloadRewarded = async () => {
  if (!IS_NATIVE_IOS) return false;
  try {
    await initAds();
    await AdMob.prepareRewardVideoAd({ adId: REWARDED_AD_ID, isTesting: USE_TEST_ADS });
    rewardedReady = true;
    return true;
  } catch (err) {
    console.warn('[Ads] preloadRewarded failed:', err?.message || err);
    rewardedReady = false;
    return false;
  }
};

export const isRewardedReady = () => rewardedReady;

/**
 * Show a rewarded ad and resolve how it went:
 *   { shown: true,  rewarded: true  } - user watched and earned the reward
 *   { shown: true,  rewarded: false } - user opened it but closed early
 *   { shown: false, rewarded: false } - no ad available / failed to show
 *
 * The gate treats `shown: false` as fail-open (allow the action through) and
 * `rewarded: true` as "proceed". No-op off native iOS. `feature` only tags
 * analytics.
 */
export const showRewarded = async (feature) => {
  if (!IS_NATIVE_IOS) return { shown: false, rewarded: false };

  if (!rewardedReady) {
    const ok = await preloadRewarded();
    if (!ok) return { shown: false, rewarded: false };
  }

  return new Promise((resolve) => {
    const handles = [];
    let earned = false;
    let settled = false;

    const cleanup = async () => {
      for (const h of handles) {
        try { (await h).remove(); } catch { /* ignore */ }
      }
    };

    const settle = async (result) => {
      if (settled) return;
      settled = true;
      rewardedReady = false; // a shown ad is consumed regardless of outcome
      await cleanup();
      resolve(result);
    };

    handles.push(
      AdMob.addListener(RewardAdPluginEvents.Rewarded, () => {
        earned = true;
        trackRewardedRewardEarned(feature);
      }),
    );
    handles.push(
      AdMob.addListener(RewardAdPluginEvents.Dismissed, () => {
        settle({ shown: true, rewarded: earned });
      }),
    );
    handles.push(
      AdMob.addListener(RewardAdPluginEvents.FailedToShow, () => {
        settle({ shown: false, rewarded: false });
      }),
    );

    trackRewardedAdWatched(feature);
    AdMob.showRewardVideoAd().catch((err) => {
      console.warn('[Ads] showRewardVideoAd failed:', err?.message || err);
      settle({ shown: false, rewarded: false });
    });

    // Safety net: never let the gate hang if no terminal event arrives.
    setTimeout(() => settle({ shown: true, rewarded: earned }), 90000);
  });
};
